import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, isInvoiceRepriceActive, isOwner, invoiceControlConfigByCode } from 'entities';

import { Switch, Modal, LabeledSelect, Button, Column, Label, Row } from 'components/Blocks';
import { PackagePlusIcon } from 'components/Icons';

import { PackageData } from './Data';

import { useToggleV2, useUser } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useConfirmation } from 'providers/Confirmation';

import { calculateNewAvailableAmount, canAttachProject } from './utils';

import { cn } from 'styles';
import { sortByName } from 'utils/array';
import { formatCurrency } from 'utils/text';

interface Props {
  attachPackageToProject: (id: string) => Promise<boolean>;
  loading: boolean;
}

export function SelectPackage({ attachPackageToProject, loading: loadingAttachment }: Props) {
  const { t } = useLang();
  const { project } = useProjectProfile();
  const user = useUser();

  const [selectedPackage, setPackage] = useState('');

  const [open, openHandlers] = useToggleV2();
  const [loadAllPackages, packageLoadHandlers] = useToggleV2();

  const { data, loading, error } = loaders.invoice.availablePackages.use({
    blockLoad: !open,

    reloadOnChangeRef: loadAllPackages,

    defaultValue: [],

    params: {
      client: loadAllPackages ? undefined : project.client,
      currency: project.currency,
    },

    parser: sortByName,
  });

  const { requestConfirmation } = useConfirmation();

  const packageData = data.find(({ id }) => id === selectedPackage);

  const close = (): void => {
    openHandlers.turnOff();
    packageLoadHandlers.turnOff();
    setPackage('');
  };

  const choosePackage = (): void => {
    requestConfirmation({
      title: `Select ${packageData?.name}?`,

      type: 'irreversible',

      text: 'This project will be added to the package and its full value will be deducted from the available amount',

      onConfirm: async () => {
        const success = await attachPackageToProject(selectedPackage);

        if (!success) return;

        close();
      },
    });
  };

  const availableAmount = calculateNewAvailableAmount(project, packageData);

  const togglePackageOption = (): void => {
    packageLoadHandlers.toggle();
    setPackage('');
  };

  const canSelectPackage = [
    isOwner(project.invoice, user),
    !isInvoiceRepriceActive(project),
    invoiceControlConfigByCode[project.invoice.status].canSelectPackage,
  ].every(Boolean);

  if (!canSelectPackage) return null;

  return (
    <>
      <Modal isOpen={open} close={close} width="min(90vw, 660px)" title="Select Package">
        <Row justifyContent="space-between">
          <Label>{t('only-client-packages')}</Label>
          <Switch checked={!loadAllPackages} onChange={togglePackageOption} size="small" />
        </Row>

        <Column gap="2">
          <LabeledSelect
            id="package"
            options={data}
            value={selectedPackage}
            onChangeDirect={setPackage}
            label={t('package') || ''}
            loading={loading || error}
            isSearchable
            labelKey="name"
          />

          <span className="text-end text-sm italic">{t('only-shown-in-project-currency')}</span>
        </Column>

        {!!packageData && (
          <Column gap="3">
            <PackageData data={packageData} />

            <span
              className={cn(
                'font-medium',
                availableAmount >= 0 ? 'text-success' : 'text-destructive',
              )}
            >
              {t('remaining-after-attached')}{' '}
              {formatCurrency(availableAmount, packageData.currency)}
            </span>
          </Column>
        )}

        <Button
          disabled={!canAttachProject(project, packageData)}
          onClick={choosePackage}
          loading={loadingAttachment}
        >
          <PackagePlusIcon size={16} />

          {t('select')}
        </Button>
      </Modal>

      <Column className="min-h-16" justifyContent="center" alignItems="center">
        <h6>{t('choose-package')}</h6>

        <Button onClick={openHandlers.turnOn}>
          <PackagePlusIcon size={16} />
          {t('select-package')}
        </Button>
      </Column>
    </>
  );
}
